// Data fetching with TanStack Query. One hook per screen's data; mutations refresh what they touch.
// Balances depend on almost everything, so after a change we simply refetch all year data.
import { useEffect, useRef } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import type {
  Account,
  AccountIn,
  AdminUser,
  AuditEntry,
  AuthState,
  BankHolidayOverride,
  CalendarJob,
  ClaimJob,
  CalendarStatus,
  ExtensionInfo,
  DaysOut,
  DoilsPage,
  EntryData,
  HmrcRates,
  HoursCurrent,
  HoursYear,
  ImportResult,
  Job,
  JobCreate,
  JobDetail,
  JobFields,
  JobsYear,
  LegacyYear,
  Overview,
  Planner,
  Role,
  RegisterIn,
  ResetInfo,
  ResetLink,
  Rules,
  RulesSave,
  SettingsPage,
  SiteSettings,
  Shift,
  ShiftIn,
  Status,
  TimesheetEntry,
  UpdateStatus,
  YearSettings,
  YearSettingsPatch,
} from "./types";

export const keys = {
  auth: ["auth"] as const,
  overview: (y: number) => ["year", y, "overview"] as const,
  planner: (y: number) => ["year", y, "planner"] as const,
  jobs: (y: number) => ["year", y, "jobs"] as const,
  doils: (y: number) => ["year", y, "doils"] as const,
  settings: (y: number) => ["year", y, "settings"] as const,
  rules: ["rules"] as const,
  adminUsers: ["admin", "users"] as const,
  adminSettings: ["admin", "settings"] as const,
  adminActivity: ["admin", "activity"] as const,
  adminUpdate: ["admin", "update"] as const,
  job: (id: number) => ["job", id] as const,
  hmrc: ["hmrc"] as const,
  calendar: ["calendar"] as const,
  hoursCurrent: ["hours", "current"] as const,
  hours: (y: number) => ["year", y, "hours"] as const,
  timesheets: (y: number) => ["year", y, "timesheets"] as const,
  claims: (y: number) => ["year", y, "claims"] as const,
};

function useRefreshData() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ predicate: (q) => !["auth", "hmrc"].includes(String(q.queryKey[0])) });
}

// ---------------------------------------------------------------- auth

export const useAuth = () => useQuery({ queryKey: keys.auth, queryFn: () => api.get<AuthState>("/auth/me") });

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api.post<AuthState>("/auth/login", v),
    onSuccess: (data) => {
      qc.clear(); // nothing from before sign-in carries over
      qc.setQueryData(keys.auth, data);
    },
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: RegisterIn) => api.post<AuthState>("/auth/register", v),
    onSuccess: (data) => {
      qc.clear();
      qc.setQueryData(keys.auth, data);
    },
  });
}

/** Put a changed account into the sign-in state, so the menu and pages show it straight away. */
function useSetAccount() {
  const qc = useQueryClient();
  return (user: Account) => qc.setQueryData<AuthState>(keys.auth, (a) => (a ? { ...a, user } : a));
}

export function useSaveAccount() {
  const setAccount = useSetAccount();
  return useMutation({
    mutationFn: (v: AccountIn) => api.put<Account>("/account", v),
    onSuccess: setAccount,
  });
}

// ---------------------------------------------------------------- password reset links (signed out)

export const useResetInfo = (token: string) =>
  useQuery({
    queryKey: ["reset", token],
    queryFn: () => api.get<ResetInfo>(`/auth/reset/${token}`),
    retry: false,
  });

export function useResetPassword(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (new_password: string) => api.post<AuthState>(`/auth/reset/${token}`, { new_password }),
    onSuccess: (data) => {
      qc.clear();
      qc.setQueryData(keys.auth, data);
    },
  });
}

// ---------------------------------------------------------------- admin

export const useAdminUsers = (enabled = true) =>
  useQuery({ queryKey: keys.adminUsers, queryFn: () => api.get<AdminUser[]>("/admin/users"), enabled });

export const useAdminSettings = () =>
  useQuery({ queryKey: keys.adminSettings, queryFn: () => api.get<SiteSettings>("/admin/settings") });

export const useAdminActivity = () =>
  useQuery({ queryKey: keys.adminActivity, queryFn: () => api.get<AuditEntry[]>("/admin/activity") });

/** The updater's status. While it's busy it's read every few seconds, through the restart (errors and all). */
export const useUpdateStatus = () =>
  useQuery({
    queryKey: keys.adminUpdate,
    queryFn: () => api.get<UpdateStatus>("/admin/update"),
    retry: false,
    refetchInterval: (q) => {
      const s = q.state.data;
      return s && (s.requested || s.update?.state === "running") ? 3000 : false;
    },
  });

/** Any admin change; afterwards the accounts, settings and activity are read again. */
function useAdminMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

export const useApproveUser = () =>
  useAdminMutation((id: number) => api.post<AdminUser>(`/admin/users/${id}/approve`));
export const useRejectUser = () =>
  useAdminMutation((id: number) => api.post<void>(`/admin/users/${id}/reject`));
export const useSetDisabled = () =>
  useAdminMutation(({ id, value }: { id: number; value: boolean }) =>
    api.put<AdminUser>(`/admin/users/${id}/disabled`, { value }),
  );
export const useSetAdmin = () =>
  useAdminMutation(({ id, value }: { id: number; value: boolean }) =>
    api.put<AdminUser>(`/admin/users/${id}/admin`, { value }),
  );
export const useResetLink = () =>
  useAdminMutation((id: number) => api.post<ResetLink>(`/admin/users/${id}/reset-link`));
export const useDeleteUser = () =>
  useAdminMutation(({ id, confirm_email }: { id: number; confirm_email: string }) =>
    api.del(`/admin/users/${id}`, { confirm_email }),
  );
export const useSaveSiteSettings = () =>
  useAdminMutation((v: SiteSettings) => api.put<SiteSettings>("/admin/settings", v));
export const useRequestUpdate = () =>
  useAdminMutation((action: "check" | "update") => api.post<UpdateStatus>("/admin/update", { action }));

export function useChangePassword() {
  const setAccount = useSetAccount();
  return useMutation({
    mutationFn: (v: { current_password: string; new_password: string }) =>
      api.put<Account>("/account/password", v),
    onSuccess: setAccount,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AuthState>("/auth/logout"),
    onSuccess: (data) => {
      qc.clear();
      qc.setQueryData(keys.auth, data);
    },
  });
}

// ---------------------------------------------------------------- reads
// Year pages read-only views keep showing the last year (dimmed, see Stale) while the next one loads,
// instead of flashing "Loading". Pages with forms don't, so a form never starts from another year.

export const useOverview = (year: number) =>
  useQuery({
    queryKey: keys.overview(year),
    queryFn: () => api.get<Overview>(`/years/${year}/overview`),
    placeholderData: keepPreviousData,
  });

export const usePlanner = (year: number) =>
  useQuery({
    queryKey: keys.planner(year),
    queryFn: () => api.get<Planner>(`/years/${year}/planner`),
    placeholderData: keepPreviousData,
  });

export const useJobsYear = (year: number) =>
  useQuery({
    queryKey: keys.jobs(year),
    queryFn: () => api.get<JobsYear>(`/years/${year}/jobs`),
    placeholderData: keepPreviousData,
  });

export const useJob = (id: number) =>
  useQuery({ queryKey: keys.job(id), queryFn: () => api.get<JobDetail>(`/jobs/${id}`), retry: false });

export const useDoils = (year: number) =>
  useQuery({ queryKey: keys.doils(year), queryFn: () => api.get<DoilsPage>(`/years/${year}/doils`) });

// The server caches these, so there's no need to refetch them during a visit.
export const useHmrcRates = () =>
  useQuery({
    queryKey: keys.hmrc,
    queryFn: () => api.get<HmrcRates>("/hmrc/overseas-rates"),
    staleTime: Infinity,
    retry: false,
  });

export const useSettingsPage = (year: number) =>
  useQuery({
    queryKey: keys.settings(year),
    queryFn: () => api.get<SettingsPage>(`/years/${year}/settings`),
  });

// ---------------------------------------------------------------- writes

export function useSetDays(year: number) {
  const qc = useQueryClient();
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (v: { dates: string[]; status: Status | "clear" | "calendar" }) =>
      api.put<DaysOut>("/days", v),
    onSuccess: (data) => {
      // Patch the sidebar figures straight away, then refetch in the background.
      qc.setQueryData<Planner>(keys.planner(year), (p) => (p ? { ...p, summary: data.summary } : p));
      refresh();
    },
  });
}

export function useCreateJob() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: (v: JobCreate) => api.post<Job>("/jobs", v), onSuccess: refresh });
}

export function useSaveJob(id: number) {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (v: { job: JobFields; entries: EntryData[] }) => api.put<Job>(`/jobs/${id}`, v),
    onSuccess: refresh,
  });
}

export function useDeleteJob() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: (id: number) => api.del(`/jobs/${id}`), onSuccess: refresh });
}

export function usePatchYearSettings(year: number) {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (v: YearSettingsPatch) => api.patch<YearSettings>(`/years/${year}/settings`, v),
    onSuccess: refresh,
  });
}

export function useSaveRules() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (v: RulesSave) => api.put<Rules>("/settings/rules", v),
    onSuccess: refresh,
  });
}

export const useRules = () =>
  useQuery({ queryKey: keys.rules, queryFn: () => api.get<Rules>("/settings/rules"), staleTime: Infinity });

/** Whether expenses are tracked (Settings). Some people only use the app for DOILs. */
export const useExpenses = () => useRules().data?.expenses_enabled ?? true;

export function useSaveRole() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (r: Role) => api.put<Role>(`/roles/${encodeURIComponent(r.code)}`, r),
    onSuccess: refresh,
  });
}

export function useDeleteRole() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (code: string) => api.del(`/roles/${encodeURIComponent(code)}`),
    onSuccess: refresh,
  });
}

export function useSaveBankHoliday() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (o: BankHolidayOverride) => api.put(`/bank-holiday-overrides/${o.date}`, o),
    onSuccess: refresh,
  });
}

export function useDeleteBankHoliday() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (date: string) => api.del(`/bank-holiday-overrides/${date}`),
    onSuccess: refresh,
  });
}

export function useSaveHistory() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (h: LegacyYear) => api.put(`/history/${h.year}`, h),
    onSuccess: refresh,
  });
}

export function useDeleteHistory() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: (year: number) => api.del(`/history/${year}`), onSuccess: refresh });
}

function uploader(path: string) {
  return (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post<ImportResult>(path, form);
  };
}

export function useImportWorkbook() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: uploader("/import/workbook"), onSuccess: refresh });
}

export function useRestore() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: uploader("/restore"), onSuccess: refresh });
}

// ---------------------------------------------------------------- hours

export const useHoursCurrent = () =>
  useQuery({
    queryKey: keys.hoursCurrent,
    queryFn: () => api.get<HoursCurrent>("/hours/current"),
    refetchInterval: 5 * 60 * 1000, // keeps the punch clock honest if the page is left open
  });

export const useHoursYear = (year: number) =>
  useQuery({
    queryKey: keys.hours(year),
    queryFn: () => api.get<HoursYear>(`/years/${year}/hours`),
    placeholderData: keepPreviousData,
  });

export function usePunchIn() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: () => api.post<Shift>("/hours/punch-in"), onSuccess: refresh });
}

export function usePunchOut() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: () => api.post<Shift>("/hours/punch-out", {}),
    onSuccess: refresh,
  });
}

/** Start or end a break while punched in. */
export function useBreak() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (to: "start" | "end") => api.post<Shift>(`/hours/break-${to}`),
    onSuccess: refresh,
  });
}

/** The year's shifts as Employment Hero timesheet entries. */
export const useTimesheets = (year: number) =>
  useQuery({
    queryKey: keys.timesheets(year),
    queryFn: () => api.get<TimesheetEntry[]>(`/timesheets?start=${year}-01-01&end=${year}-12-31`),
  });

/** The year's jobs as Concur expense claims: which have been claimed (by the browser extension). */
export const useClaims = (year: number, enabled = true) =>
  useQuery({
    queryKey: keys.claims(year),
    queryFn: () => api.get<ClaimJob[]>(`/claims?start=${year}-01-01&end=${year}-12-31`),
    placeholderData: keepPreviousData,
    enabled,
  });

/** Mark a whole job as claimed in Concur by hand, or back to to claim (clearing every line). */
export function useMarkJobClaimed(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, claimed }: { jobId: number; claimed: boolean }) => {
      if (claimed) await api.put<ClaimJob>(`/claims/${jobId}/claimed`);
      else await api.del(`/claims/${jobId}/lines`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.claims(year) }),
  });
}

/** Mark days as put into Employment Hero (submitted = true), or undo that for one day. */
export function useMarkSubmitted() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: async ({ dates, submitted }: { dates: string[]; submitted: boolean }) => {
      if (submitted) await api.put<TimesheetEntry[]>("/timesheets/submissions", { dates, method: "manual" });
      else await api.del(`/timesheets/${dates[0]}/submission`);
    },
    onSuccess: refresh,
  });
}

export function useSaveShift() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: ({ id, shift }: { id: number | null; shift: ShiftIn }) =>
      id === null ? api.post<Shift>("/shifts", shift) : api.put<Shift>(`/shifts/${id}`, shift),
    onSuccess: refresh,
  });
}

export function useDeleteShift() {
  const refresh = useRefreshData();
  return useMutation({ mutationFn: (id: number) => api.del(`/shifts/${id}`), onSuccess: refresh });
}

// ---------------------------------------------------------------- browser extension

export const useExtensionInfo = () =>
  useQuery({
    queryKey: ["extension"],
    queryFn: () => api.get<ExtensionInfo>("/extension"),
    staleTime: Infinity,
  });

// ---------------------------------------------------------------- work calendar

/** The work calendar's status. While it's being read (it can take a minute), checks every few seconds,
 * then refreshes everything so the planner shows the new days. */
export function useCalendar(known?: CalendarStatus) {
  const refresh = useRefreshData();
  const q = useQuery({
    queryKey: keys.calendar,
    queryFn: () => api.get<CalendarStatus>("/calendar"),
    // The planner's response already has the status: start from it instead of asking again.
    initialData: known,
    initialDataUpdatedAt: known ? Date.now() : undefined,
    refetchInterval: (query) => (query.state.data?.syncing ? 3000 : false),
  });
  const syncing = q.data?.syncing;
  const was = useRef(syncing);
  useEffect(() => {
    if (was.current && syncing === false) void refresh();
    was.current = syncing;
  }, [syncing, refresh]);
  return q;
}

export function useSaveCalendar() {
  const qc = useQueryClient();
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (url: string | null) => api.put<CalendarStatus>("/calendar", { url }),
    onSuccess: (data) => {
      void refresh();
      qc.setQueryData(keys.calendar, data);
    },
  });
}

export function useSyncCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CalendarStatus>("/calendar/sync"),
    onSuccess: (data) => qc.setQueryData(keys.calendar, data),
  });
}

export function useCreateCalendarJob() {
  const refresh = useRefreshData();
  return useMutation({
    mutationFn: (j: CalendarJob) =>
      api.post<Job>("/calendar/jobs", { name: j.name, dates: j.dates, location: j.location }),
    onSuccess: refresh,
  });
}
